import { useState } from "react";
import { Send, Mail, MessageSquare, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const CONTACT_INFO = [
  { icon: Mail, label: "Email", value: "hello@tyosolution.com" },
  { icon: MessageSquare, label: "Response", value: "Within 24 hours" },
  { icon: MapPin, label: "Region", value: "Indonesia (Asia Pacific)" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 px-6 md:px-10 section-light" data-testid="contact-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left */}
          <div>
            <span className="overline-tag block mb-3">Contact</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-5">
              Start automating<br />today.
            </h2>
            <p className="text-[#94A3B8] text-lg leading-relaxed mb-10">
              Join businesses already using Tyo Solution to automate invoices, optimize SEO for AI,
              and track industry changes in real time.
            </p>

            <div className="space-y-5 mb-10">
              {CONTACT_INFO.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#38BDF8]/10 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-[#38BDF8]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#64748B] font-semibold uppercase tracking-wider">{label}</div>
                    <div className="text-white text-sm mt-0.5 font-medium">{value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-[#64748B] mb-4">Our products</div>
              <div className="grid grid-cols-3 gap-3">
                {["OCR API", "ScoreMyCV", "PDF Splitter", "Image Optimizer", "News Tracking"].map(badge => (
                  <div key={badge} className="border border-white/[0.08] rounded-xl px-3 py-2 text-center bg-white/[0.02]">
                    <span className="text-xs text-[#94A3B8] font-medium">{badge}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="glass-card p-8 relative">
            <div className="absolute top-0 left-6 right-6 h-px bg-[#38BDF8]/30 rounded-full" />

            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="contact-success">
                <div className="w-16 h-16 rounded-2xl bg-[#38BDF8]/10 flex items-center justify-center mb-5">
                  <CheckCircle2 size={30} className="text-[#38BDF8]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Message Received</h3>
                <p className="text-[#94A3B8] text-sm">We'll be in touch within 24 hours.</p>
                <button onClick={() => setStatus(null)} className="mt-6 text-xs text-[#38BDF8] border border-[#38BDF8]/30 px-4 py-2 rounded-lg hover:bg-[#38BDF8]/10 transition-colors">
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h3 className="font-semibold text-white text-lg mb-6">Get Early Access</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider block mb-2">Name *</label>
                    <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="John Doe" className="input-field" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider block mb-2">Email *</label>
                    <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john@company.com" className="input-field" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider block mb-2">Company</label>
                  <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Acme Corp" className="input-field" data-testid="contact-company-input" />
                </div>
                <div className="mb-5">
                  <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider block mb-2">Message *</label>
                  <textarea required rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your document processing needs..." className="input-field resize-none" data-testid="contact-message-input" />
                </div>
                {status === "error" && (
                  <div className="mb-4 flex items-center gap-2 text-red-400 text-sm bg-red-400/[0.08] border border-red-400/20 p-3 rounded-xl" data-testid="contact-error">
                    <AlertCircle size={14} /> {errMsg}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className={`w-full flex items-center justify-center gap-2 py-3.5 font-semibold text-sm rounded-xl transition-all ${
                    status === "loading"
                      ? "bg-white/[0.05] text-[#64748B] cursor-not-allowed"
                      : "bg-[#38BDF8] text-[#080f23] hover:bg-[#0EA5E9] shadow-[0_0_20px_rgba(56,189,248,0.2)]"
                  }`}
                  data-testid="contact-submit-btn"
                >
                  {status === "loading" ? (
                    <><div className="w-4 h-4 border-2 border-[#64748B] border-t-transparent rounded-full animate-spin" /> Sending...</>
                  ) : (
                    <><Send size={15} /> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
